import React, { useState, useEffect, useMemo } from 'react';
import FilterIcon from '../../SVG/Filter';
import EmptyHeader from '../Shared/EmptyHeader';
import NoteModal from "./NoteModal";
import AgentInfoModal from "./AgentInfoModal";
import EditModal from "./EditModal";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../src/Context/AuthContext";

const UnderwritingSection = ({ rowsPerPage = 10 }) => {
    // Auth
    const { user, loading: authLoading, logout } = useAuth();
    // --- Controlled inputs for filters ---
    const [filterPolNoInput, setFilterPolNoInput] = useState('');
    const [filterFirstNameInput, setFilterFirstNameInput] = useState('');
    const [filterLastNameInput, setFilterLastNameInput] = useState('');
    const [filterStatusInput, setFilterStatusInput] = useState('');
    const [filterReceivedDateInput, setFilterReceivedDateInput] = useState('');

    // --- Data states ---
    const [policies, setPolicies] = useState([]);
    const [statuses, setStatuses] = useState([]);
    const [loading, setLoading] = useState(true);

    // --- Pagination ---
    const [currentPage, setCurrentPage] = useState(1);

    // --- Sorting ---
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    // ENV
    const companyName = import.meta.env.VITE_COMPANY_NAME;
    const apiURL = import.meta.env.VITE_API;

    document.title = `${companyName} - Policy Approval`;

    const navigate = useNavigate();

    const handleNavigateToImages = (polNo) => {
        navigate("/images", { state: { polNo } });
    };

    // Redirect if user role is >= 3
    useEffect(() => {
        if (!loading) { // only check after context finishes loading
            if (!user || user.hasUW != "true") {
                navigate('/home');
            }
        }
    }, [user, loading, navigate]);

    // Fetch statuses
    const getUnderwritingStatuses = async () => {
        try {
            const res = await fetch(`${apiURL}/underwriting/GetUnderwritingStatusesToView`, {
                method: 'GET',
                credentials: "include"
            });

            if (res.status === 401) {
                logout();
                navigate('/login');
                throw new Error("unauthorized");
            }

            if (res.status === 403) {
                navigate('/home');
                throw new Error("forbidden");
            }

            if (!res.ok) {
                throw new Error('Failed to fetch statuses');
            }

            const data = await res.json();
            return Array.isArray(data) ? data : [];
        } catch (err) {
            console.error('Error fetching statuses:', err);
            return [];
        }
    };

    // Fetch policies
    const getUnderwritingPolicies = async () => {
        try {
            const res = await fetch(`${apiURL}/underwriting/GetUnderwritingPolicies`, {
                method: 'GET',
                credentials: "include"
            });

            if (res.status === 401) {
                logout();
                navigate('/login');
                throw new Error("unauthorized");
            }

            if (res.status === 403) {
                navigate('/home');
                throw new Error("forbidden");
            }

            if (!res.ok) {
                throw new Error('Failed to fetch underwriting policies');
            }
            const data = await res.json();
            return Array.isArray(data) ? data : [];
        } catch (err) {
            console.error('Error fetching policies:', err);
            return [];
        }
    };

    // Helper to refresh policies (used after successful save)
    const refreshPolicies = async () => {
        const policyData = await getUnderwritingPolicies();
        setPolicies(policyData);
    };

    // Initial load
    useEffect(() => {
        const loadInitialData = async () => {
            setLoading(true);
            const [statusData, policyData] = await Promise.all([
                getUnderwritingStatuses(),
                getUnderwritingPolicies()
            ]);
            setStatuses(statusData);
            setPolicies(policyData);
            setLoading(false);
        };
        loadInitialData();
    }, []);

    // Sorting handler
    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc'
                ? 'desc'
                : 'asc'
        }));
    };

    const getSortIndicator = (key) => {
        if (sortConfig.key !== key) return "";
        return sortConfig.direction === "asc" ? " ▲" : " ▼";
    };

    // Filtering + Sorting
    const filteredPolicies = useMemo(() => {
        const filtered = policies.filter(p => {
            const policyDate = p.recDate
                ? new Date(p.recDate).toISOString().split('T')[0]
                : null;

            return (
                (!filterPolNoInput || p.polNo?.toLowerCase().includes(filterPolNoInput.toLowerCase())) &&
                (!filterFirstNameInput || p.firstName?.toLowerCase().includes(filterFirstNameInput.toLowerCase())) &&
                (!filterLastNameInput || p.lastName?.toLowerCase().includes(filterLastNameInput.toLowerCase())) &&
                (!filterStatusInput || p.status === filterStatusInput) &&
                (!filterReceivedDateInput || policyDate === filterReceivedDateInput)
            );
        });

        if (!sortConfig.key) return filtered;

        return [...filtered].sort((a, b) => {
            let aVal = a[sortConfig.key] ?? "";
            let bVal = b[sortConfig.key] ?? "";

            let comparison = 0;

            if (sortConfig.key.toLowerCase().includes("date")) {
                const aDate = aVal ? new Date(aVal) : new Date(0);
                const bDate = bVal ? new Date(bVal) : new Date(0);
                comparison = aDate - bDate;
            } else {
                comparison = aVal
                    .toString()
                    .localeCompare(bVal.toString(), undefined, { numeric: true });
            }

            return sortConfig.direction === "asc" ? comparison : -comparison;
        });

    }, [
        policies,
        filterPolNoInput,
        filterFirstNameInput,
        filterLastNameInput,
        filterStatusInput,
        filterReceivedDateInput,
        sortConfig
    ]);

    // Pagination
    const totalEntries = filteredPolicies.length;
    const totalPages = Math.ceil(totalEntries / rowsPerPage);

    const paginatedDocs = useMemo(() => {
        const start = (currentPage - 1) * rowsPerPage;
        return filteredPolicies.slice(start, start + rowsPerPage);
    }, [filteredPolicies, currentPage, rowsPerPage]);

    useEffect(() => {
        setCurrentPage(1);
    }, [filteredPolicies]);

    const from = totalEntries === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
    const to = Math.min(currentPage * rowsPerPage, totalEntries);

    // Modals
    const [showNotesModal, setShowNotesModal] = useState(false);
    const [selectedNotes, setSelectedNotes] = useState([]);

    const handleOpenNotes = (notes) => {
        setSelectedNotes(notes || []);
        setShowNotesModal(true);
    };

    const [showAgentInfoModal, setShowAgentInfoModal] = useState(false);
    const [selectedAgentInfo, setSelectedAgentInfo] = useState([]);

    const handleOpenAgentInfo = (agentInfo) => {
        setSelectedAgentInfo(agentInfo || []);
        setShowAgentInfoModal(true);
    };

    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedPolicy, setSelectedPolicy] = useState([]);

    const handleUnlockPolicy = async (polNo) => {
        try {
            const res = await fetch(`${apiURL}/underwriting/UnlockPolicy?PolNo=${polNo}`, {
                method: 'POST',
                credentials: "include"
            });

            if (res.status === 401) {
                logout();
                navigate('/login');
                throw new Error("unauthorized");
            }

            if (res.status === 403) {
                navigate('/home');
                throw new Error("forbidden");
            }

            if (res.ok) {
                return true;
            }

            let message = "Unable to unlock policy.";

            try {
                const data = await res.json();
                if (data?.message) {
                    message = data.message;
                }
            } catch {
                // If JSON parsing fails, keep default message
            }

            alert(message);
            return false;

        } catch (err) {
            console.error('Error locking policy:', err);
            return false;
        }
    };

    const handleCloseEditModal = async (policy) => {
        setShowEditModal(false);
        if (policy?.polNo) {
            await handleUnlockPolicy(policy.polNo);
        }
        setSelectedPolicy([]);
    };

    const handleLockPolicy = async (polNo) => {
        try {
            const res = await fetch(`${apiURL}/underwriting/LockPolicy?PolNo=${polNo}`, {
                method: 'POST',
                credentials: "include"
            });

            if (res.status === 401) {
                logout();
                navigate('/login');
                throw new Error("unauthorized");
            }

            if (res.status === 403) {
                navigate('/home');
                throw new Error("forbidden");
            }

            if (res.ok) {
                return true;
            }

            let message = "Unable to lock policy.";

            try {
                const data = await res.json();
                if (data?.message) {
                    message = data.message;
                }
            } catch {
                // If JSON parsing fails, keep default message
            }

            alert(message);
            return false;

        } catch (err) {
            console.error('Error locking policy:', err);
            return false;
        }
    };

    const handleOpenEdit = async (policy) => {
        const locked = await handleLockPolicy(policy.polNo);

        if (!locked) {
            return;
        }
        setSelectedPolicy(policy || []);
        setShowEditModal(true);
    };

    // Called when EditModal reports a successful save
    const handleEditSaved = async () => {
        await refreshPolicies();
    };

    return (
        <>
            <EmptyHeader />

            <div className="container container-section" id="underwriting-container">

                {/* --- Filters --- */}
                <form className="filter-box">
                    <FilterIcon />

                    <div className="filter-item">
                        <label className="filterLabel">Policy Number</label>
                        <input
                            className="filterInput"
                            value={filterPolNoInput}
                            onChange={e => setFilterPolNoInput(e.target.value)}
                        />
                    </div>

                    <div className="filter-item">
                        <label className="filterLabel">First Name</label>
                        <input
                            className="filterInput"
                            value={filterFirstNameInput}
                            onChange={e => setFilterFirstNameInput(e.target.value)}
                        />
                    </div>

                    <div className="filter-item">
                        <label className="filterLabel">Last Name</label>
                        <input
                            className="filterInput"
                            value={filterLastNameInput}
                            onChange={e => setFilterLastNameInput(e.target.value)}
                        />
                    </div>

                    <div className="filter-item">
                        <label className="filterLabel">Status</label>
                        <select
                            className="filterInput"
                            value={filterStatusInput}
                            onChange={e => setFilterStatusInput(e.target.value)}
                        >
                            <option value="">All</option>
                            {statuses.map((status, idx) => (
                                <option key={idx} value={status.status}>{status.status} - {status.clientDesc || status.amrDesc}</option>
                            ))}
                        </select>
                    </div>

                    <div className="filter-item">
                        <label className="filterLabel">Received Date</label>
                        <input
                            type="date"
                            className="filterInput"
                            value={filterReceivedDateInput}
                            onChange={e => setFilterReceivedDateInput(e.target.value)}
                        />
                    </div>

                    <div className="filter-button-box">
                        <button
                            type="button"
                            className="filterButton"
                            onClick={() => {
                                setFilterPolNoInput('');
                                setFilterFirstNameInput('');
                                setFilterLastNameInput('');
                                setFilterStatusInput('');
                                setFilterReceivedDateInput('');
                            }}
                        >
                            Clear
                        </button>
                    </div>
                </form>

                {/* --- Results --- */}
                {loading ? (
                    <div className="card notfoundbox rounded-3 shadow-lg"><p>Loading data...</p></div>
                ) : filteredPolicies.length === 0 ? (
                    <div className="card notfoundbox rounded-3 shadow-lg"><h5>No Results Found</h5></div>
                ) : (
                    <>
                        <div className="paginated-table-container">
                            <table className="paginated-table">
                                <thead>
                                    <tr>
                                        <th onClick={() => handleSort("polNo")} style={{ cursor: "pointer" }}>
                                            Policy No{getSortIndicator("polNo")}
                                        </th>
                                        <th onClick={() => handleSort("firstName")} style={{ cursor: "pointer" }}>
                                            First Name{getSortIndicator("firstName")}
                                        </th>
                                        <th onClick={() => handleSort("lastName")} style={{ cursor: "pointer" }}>
                                            Last Name{getSortIndicator("lastName")}
                                        </th>
                                        <th onClick={() => handleSort("status")} style={{ cursor: "pointer" }}>
                                            Status{getSortIndicator("status")}
                                        </th>
                                        <th onClick={() => handleSort("recDate")} style={{ cursor: "pointer" }}>
                                            Received Date{getSortIndicator("recDate")}
                                        </th>
                                        <th onClick={() => handleSort("initialPremiumAmount")} style={{ cursor: "pointer" }}>
                                            Initial Premium{getSortIndicator("initialPremiumAmount")}
                                        </th>
                                        <th onClick={() => handleSort("plan")} style={{ cursor: "pointer" }}>
                                            Plan{getSortIndicator("plan")}
                                        </th>
                                        <th>Agent Info</th>
                                        <th>Edit</th>
                                        <th>Notes</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedDocs.map((p, i) => (
                                        <tr key={i}>
                                            <td>
                                                <button className="linkText" onClick={() => handleNavigateToImages(p.polNo)}>
                                                    {p.polNo}
                                                </button>
                                            </td>
                                            <td>{p.firstName}</td>
                                            <td>{p.lastName}</td>
                                            <td>{p.status}</td>
                                            <td>{p.recDate}</td>
                                            <td>{p.initialPremiumAmount}</td>
                                            <td>{p.plan}</td>
                                            <td>
                                                <button className="linkText" onClick={() => handleOpenAgentInfo(p.agentHierarchy)}>
                                                    Agent Info
                                                </button>
                                            </td>
                                            <td>
                                                <button className="linkText" onClick={() => handleOpenEdit(p)}>
                                                    Edit
                                                </button>
                                            </td>
                                            <td>
                                                <button className="linkText" onClick={() => handleOpenNotes(p.notes)}>
                                                    Notes
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            {/* Pagination */}
                            <div className="pagination-bar">
                                <div className="pagination-summary">
                                    Showing {from}–{to} of {totalEntries} entries
                                </div>

                                <div className="pagination-controls">
                                    <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>«</button>
                                    <button onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1}>‹</button>

                                    {[...Array(totalPages)].map((_, i) => {
                                        const page = i + 1;
                                        if (page < currentPage - 1 || page > currentPage + 1) return null;
                                        return (
                                            <button
                                                key={page}
                                                onClick={() => setCurrentPage(page)}
                                                className={currentPage === page ? 'active' : ''}
                                            >
                                                {page}
                                            </button>
                                        );
                                    })}

                                    <button onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages}>›</button>
                                    <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>»</button>
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {/* Modals */}
                <NoteModal
                    isOpen={showNotesModal}
                    onClose={() => setShowNotesModal(false)}
                    notes={selectedNotes}
                />

                <AgentInfoModal
                    isOpen={showAgentInfoModal}
                    onClose={() => setShowAgentInfoModal(false)}
                    agentInfo={selectedAgentInfo}
                />

                <EditModal
                    isOpen={showEditModal}
                    onClose={() => handleCloseEditModal(selectedPolicy)}
                    onSaved={handleEditSaved}
                    policy={selectedPolicy}
                />
            </div>
        </>
    );
};

export default UnderwritingSection;
